import { useState, useEffect } from 'react'
import { isNarrowViewport } from '../lib/windowGeometry'

const SnapZones = ({ isDragging, mousePosition, onSnapPreview, onSnapRelease }) => {
  const [activeZone, setActiveZone] = useState(null)
  const [snapThreshold] = useState(15) // pixels from edge to trigger snap

  // Define snap zones
  const getSnapZones = () => {
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    
    return {
      left: {
        id: 'left',
        bounds: { x: 0, y: 0, width: snapThreshold, height: viewportHeight },
        position: { x: 0, y: 0 },
        size: { width: viewportWidth / 2, height: viewportHeight },
        label: 'Snap Left'
      },
      right: {
        id: 'right',
        bounds: { x: viewportWidth - snapThreshold, y: 0, width: snapThreshold, height: viewportHeight },
        position: { x: viewportWidth / 2, y: 0 },
        size: { width: viewportWidth / 2, height: viewportHeight },
        label: 'Snap Right'
      },
      top: {
        id: 'top',
        bounds: { x: 0, y: 0, width: viewportWidth, height: snapThreshold },
        position: { x: 0, y: 0 },
        size: { width: viewportWidth, height: viewportHeight },
        label: 'Maximize'
      },
      topLeft: {
        id: 'top-left',
        bounds: { x: 0, y: 0, width: snapThreshold * 2, height: snapThreshold * 2 },
        position: { x: 0, y: 0 },
        size: { width: viewportWidth / 2, height: viewportHeight / 2 },
        label: 'Top Left Quarter'
      },
      topRight: {
        id: 'top-right',
        bounds: { x: viewportWidth - snapThreshold * 2, y: 0, width: snapThreshold * 2, height: snapThreshold * 2 },
        position: { x: viewportWidth / 2, y: 0 },
        size: { width: viewportWidth / 2, height: viewportHeight / 2 },
        label: 'Top Right Quarter'
      },
      bottomLeft: {
        id: 'bottom-left',
        bounds: { x: 0, y: viewportHeight - snapThreshold * 2, width: snapThreshold * 2, height: snapThreshold * 2 },
        position: { x: 0, y: viewportHeight / 2 },
        size: { width: viewportWidth / 2, height: viewportHeight / 2 },
        label: 'Bottom Left Quarter'
      },
      bottomRight: {
        id: 'bottom-right',
        bounds: { x: viewportWidth - snapThreshold * 2, y: viewportHeight - snapThreshold * 2, width: snapThreshold * 2, height: snapThreshold * 2 },
        position: { x: viewportWidth / 2, y: viewportHeight / 2 },
        size: { width: viewportWidth / 2, height: viewportHeight / 2 },
        label: 'Bottom Right Quarter'
      }
    }
  }

  // Check if mouse is in snap zone
  const checkSnapZone = (mouseX, mouseY) => {
    const zones = getSnapZones()

    // A half or a quarter of a phone screen is not a usable window, and the
    // 15px edge zones are far too easy to clip with a finger while dragging.
    // Narrow screens keep only the top zone, which maximises.
    const usable = isNarrowViewport()
      ? { top: zones.top }
      : zones

    for (const [key, zone] of Object.entries(usable)) {
      const { x, y, width, height } = zone.bounds
      if (mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height) {
        return zone
      }
    }
    
    return null
  }

  // Update active zone when mouse moves during drag
  useEffect(() => {
    if (isDragging && mousePosition) {
      const zone = checkSnapZone(mousePosition.x, mousePosition.y)
      
      if (zone !== activeZone) {
        setActiveZone(zone)
        onSnapPreview?.(zone)
      }
    } else if (!isDragging) {
      setActiveZone(null)
      onSnapPreview?.(null)
    }
  }, [isDragging, mousePosition, activeZone, onSnapPreview])

  // Handle snap release
  useEffect(() => {
    if (!isDragging && activeZone) {
      onSnapRelease?.(activeZone)
      setActiveZone(null)
    }
  }, [isDragging, activeZone, onSnapRelease])

  if (!isDragging || !activeZone) return null

  return (
    <>
      {/* Snap Zone Preview Overlay */}
      <div
        className="fixed pointer-events-none z-[9999] border-2 border-blue-400 bg-blue-400/20 transition-all duration-150"
        style={{
          left: activeZone.position.x,
          top: activeZone.position.y,
          width: activeZone.size.width,
          height: activeZone.size.height,
        }}
      >
        {/* Snap Zone Label */}
        <div className="absolute top-4 left-4 bg-blue-500/90 text-white px-3 py-1 rounded-md text-sm font-medium">
          {activeZone.label}
        </div>
      </div>
    </>
  )
}

export default SnapZones
